import { IMicro, IMicroInterlink, IMicroConfiguration, TMicroPlugin } from '../types/configuration';
import { TSubscription } from '../types/subscription';
import { ViewRouteTable } from '../transformations/view_route_table';
import { ViewRouteTree } from '../transformations/view_route_tree';
import { ViewRenderProcess } from '../processes/view_render';
import { MicroModelProcess } from '../processes/micro_model';
import { ViewStateProcess } from '../processes/view_state';
import { ViewModelProcess } from '../processes/view_model';
import { RoutingProcess } from '../processes/routing';
import { MicroView } from './view';

import { t_event_message, IUnsafeRouteTree, IRouteRequest } from'../../../dip/library/di-hnf/di-hnf.js';
import { Kernel, View, IProcessDelegate, Ignition } from'../../../pip/library/pi-interop/pi-interop.js';
import navigation from'../../../pip/library/pi-navigation/pi-navigation.js';
import $ip from'../../../pip/library/pi-ip/pi-ip.js';
import f1 from'../../../pip/library/pi-f1/pi-f1.js';

export interface IMicroKernelProcesses extends Record<string, IProcessDelegate> {
  view_render: ViewRenderProcess;
  micro_model: MicroModelProcess;
  view_state: ViewStateProcess;
  view_model: ViewModelProcess;
  routing: RoutingProcess;
}

/**
 *
 */
export class Micro<C extends IMicroConfiguration> extends View implements IMicro<C> {
  readonly kernel: Kernel<IMicroKernelProcesses> = new Kernel<IMicroKernelProcesses>('micro');
  readonly view: MicroView = new MicroView(this);
  readonly ignition: Ignition = new Ignition();

  /**
   * every micro should create a name for itself
   */
  id: string;

  /**
   * the final configuration controlling the Micro
   */
  configuration: C;

  /**
   * to modify the configuration at the end of initialize
   */
  plugins: TMicroPlugin[] = [];

  /**
   * if you create a Micro class, then you can initial the Micro with
   * an internal configuration
   * @virtual
   * @abstract
   */
  protected _initialConfiguration(): Partial<C> {
    return {};
  }

  /**
   *
   */
  protected _subscribers: Map<t_event_message, TSubscription> = new Map();

  /**
   *
   */
  constructor() {
    super();

    f1.each((process) => this.kernel.register(process), [
      new MicroModelProcess(),
      new ViewStateProcess(this),
      new ViewRenderProcess(),
      new ViewModelProcess(),
      new RoutingProcess()
    ]);
  }

  private __interlink: IMicroInterlink = {
    location_uri: '/',
    resource_uri: '',
    source_uri: '',
    configuration: {} as IMicroConfiguration,
    import: {
      plugins: []
    }
  };

  interlinkConfiguration(interlink: IMicroInterlink) {
    this.__interlink = f1.merge('over', this.__interlink, interlink || {});
    return this;
  }

  private __proxyConfiguration: Partial<C> = {};

  proxyConfiguration(proxyConfiguration: Partial<C>) {
    this.__proxyConfiguration = proxyConfiguration || {};
    return this;
  }

  /**
   * override this method to augment the configuration or use it before the Micro has started and return the configuration;
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected _configure(configuration: C) {
    return configuration;
  }

  /**
   *
   */
  async startup(region: HTMLElement, configuration: C) {
    await this.ignition.startup(this, configuration);
    await this.ignition.mount(this, region);
    return this;
  }

  /**
   *
   */
  async shutdown() {
    await this.ignition.unmount(this);
    await this.ignition.shutdown(this);
    return this;
  }

  async initialize(configuration: C) {
    const interlink = this.__interlink;
    const c1: C = f1.merge('over', this.__proxyConfiguration, configuration);
    const c2: C = f1.merge(
      'over',
      {
        id: `micro-${f1.ascending()}`,
        externals: [],
        routes: {
          parameters: {}
        },
        view: {
          environment: 'prod',
          configuration: {},
          parameters: {},
          constants: {}
        }
      },
      c1
    );
    const c3: C = f1.merge('over', this._initialConfiguration(), c2);
    const c4: C = f1.merge('over', c3, interlink.configuration);
    const c5: C = f1.merge('over', c4, {
      routes: {
        uri: f1.read('/routes/uri', undefined, c4) || interlink.location_uri,
        resource_uri: f1.read('/routes/resource_uri', undefined, c4) || interlink.resource_uri
      }
    });

    const microConfiguration = f1.clone(f1.safe({}, this._configure(c5)) as Record<string, unknown>) as C;

    this.configuration = await this.__applyConfigurationPlugins(microConfiguration);
    // if we create a micro it should set it's own name
    // if the micro is automatically generated then we need an id on the configuration
    //
    this.configuration.id = this.id || this.configuration.id;
    return this;
  }

  /**
   * permit all micro configuration plugins to alter the configuration
   */
  private async __applyConfigurationPlugins(microConfiguration: C): Promise<C> {
    const ignition: Ignition = new Ignition();

    for await (const plugin of this.plugins) {
      await ignition.startup(plugin, microConfiguration);
      const improvedMicroConfiguration = await plugin.configure(microConfiguration);

      if (f1.is.exists(improvedMicroConfiguration)) {
        microConfiguration = f1.merge('onto', microConfiguration, improvedMicroConfiguration);
      }
    }

    return microConfiguration;
  }

  async activate() {
    // all micro should know their unique name and register a base_path for themselves
    // @question should we erase this on deactivate?
    //
    $ip.constants.upgrade({
      [`@path-micro-${this.id}`]: this.configuration.routes.uri
    });

    return this;
  }

  mount(region: HTMLElement): Promise<this> {
    const microConfiguration = this.configuration;
    const kernelConfiguration: Record<string, C> = {
      view_render: microConfiguration,
      micro_model: microConfiguration,
      view_state: microConfiguration,
      view_model: microConfiguration,
      routing: microConfiguration
    };

    this.region = region;

    return this.kernel
      .startup(kernelConfiguration, {
        view_render: {
          root: region
        }
      })
      .then(() => this);
  }

  async deactivate() {
    await this.kernel.shutdown();
    return this;
  }

  /**
   * A component may not return a Micro, but a different object. So we can get the Micro
   * from the $delegate
   */
  $delegate() {
    return this;
  }

  /**
   * The default Micro will return itself to satisfy the contract. The `$view` contract exists
   * so that a View which is not a Micro can create a Micro as a composite and return and manage
   * it's life-cycle otherwise
   */
  $view() {
    return this.view;
  }

  /**
   * update the internal state tree
   */
  includeRoutes(view_stateTree: IUnsafeRouteTree) {
    this.kernel.processes.view_model.includeRoutes(view_stateTree);
  }

  /**
   * read the browser state and reflect to the micro
   */
  async reflectBrowserState() {
    return this.transitionTo(this.kernel.processes.routing.activeRoute);
  }

  /**
   *
   */
  async transitionTo(stateRequest: IRouteRequest) {
    const processes = this.kernel.processes;

    processes.view_state.transitionTo(stateRequest);
    navigation.location.update(stateRequest.parameters);

    return this.kernel.processes.routing.activeRoute;
  }

  /**
   *
   */
  subscribe(event: t_event_message, cb: TSubscription) {
    const subscriber = this.subscriber(event);
    if (!subscriber) {
      this._subscribers.set(event, cb);
    }

    return this;
  }

  /**
   *
   */
  unsubscribe(event: t_event_message) {
    this._subscribers.delete(event);
    return this;
  }

  /**
   *
   */
  subscriber(event: t_event_message) {
    return this._subscribers.get(event);
  }

  /**
   * transform unsafe routes to safe routes
   * @param stateTree
   */
  generateRouteTree(stateTree: IUnsafeRouteTree) {
    return new ViewRouteTree().read(stateTree);
  }

  /**
   * transform unsafe routes to route table
   * @param stateTree
   */
  generateRouteTable(stateTree: IUnsafeRouteTree) {
    return new ViewRouteTable().read(this.generateRouteTree(stateTree));
  }
}
